import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import mfaService from '../../services/mfa';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';

function OrgSecuritySettings() {
  const timezone = useOrgTimezone();
  const [required, setRequired] = useState(false);
  const [requiredSince, setRequiredSince] = useState<string | null>(null);
  const [gracePeriodDays, setGracePeriodDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await mfaService.getOrgPolicy();
        if (p) {
          setRequired(p.mfa_required);
          setRequiredSince(p.mfa_required_since);
          setGracePeriodDays(p.mfa_grace_period_days || 14);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load policy');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setError(null); setInfo(null); setBusy(true);
    try {
      await mfaService.setOrgPolicy(required, gracePeriodDays);
      setInfo('Policy updated.');
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const graceEnds = requiredSince
    ? new Date(new Date(requiredSince).getTime() + gracePeriodDays * 24 * 60 * 60 * 1000)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50">
      <PageHeader
        title="Organisation security"
        subtitle="Manage two-factor authentication requirements for your organisation"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />

      <div className="max-w-2xl mx-auto py-6 px-4 space-y-6">
      {loading ? (
        <div className="p-8">Loading…</div>
      ) : (
      <>
      {error && (
        <div className="rounded-lg bg-error-50 border border-error-200 p-3 text-sm text-error-800">{error}</div>
      )}
      {info && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">{info}</div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Require two-factor authentication</CardTitle>
          <CardDescription>
            All members will need to enrol a 2FA method before the grace period expires.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 mr-2"
            />
            <span className="text-sm text-neutral-900">Require 2FA for all members</span>
          </label>

          {required && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Grace period (days)
              </label>
              <input
                type="number"
                min={0}
                max={90}
                value={gracePeriodDays}
                onChange={(e) => setGracePeriodDays(parseInt(e.target.value, 10) || 0)}
                className="block w-32 rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Existing users who haven't enrolled can still log in during this window.
              </p>
              {graceEnds && (
                <p className="text-xs text-neutral-500 mt-1">
                  Current grace period ends: <strong>{formatDateInTz(graceEnds.toISOString(), timezone)}</strong>
                </p>
              )}
            </div>
          )}

          <Button onClick={save} isLoading={busy}>Save</Button>
        </CardContent>
      </Card>
      </>
      )}
      </div>
    </div>
  );
}

export default OrgSecuritySettings;
